import { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';

interface PreAuthLayoutProps {
  children: ReactNode;
}

/**
 * PreAuthLayout — the unauthenticated (public) screen wrapper.
 *
 * Provides the brand gradient background and centred logo for all pre-auth
 * screens (Landing, Login, SignUp, Invite).
 *
 * Each child screen is responsible for its own surface/card treatment:
 * - Login, SignUp, Landing: use `Card` with `max-w-sm md:max-w-md` constraint
 * - Invite and similar full-bleed screens: use `ScreenCard` from `@/components/layout`
 *
 * The logo links to `/` (root — which will redirect authenticated users
 * to /dashboard automatically).
 */
export function PreAuthLayout({ children }: PreAuthLayoutProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isInviteLanding = pathname === '/invite';

  return (
    <div
      className={`min-h-screen ${isInviteLanding ? 'bg-gradient-brand' : 'bg-page'} flex flex-col items-center justify-start pt-8 md:pb-8 md:justify-center md:py-12`}
      data-testid="pre-auth-layout"
    >
      {/* Logo — sits above the content surface in the gradient area */}
      <Link
        to="/"
        className="mb-8 no-underline shrink-0"
        aria-label="Influenster for Creators home"
      >
        <img src="/assets/images/logo-black.svg" alt="Influenster for Creators" className="h-12" />
      </Link>

      {/* Content area — flex-1 so ScreenCard children can stretch to fill remaining height on mobile */}
      <div className="w-full flex-1 flex flex-col">{children}</div>
    </div>
  );
}
